<template>
  <b-modal ref="modal" title="New Transfer" size="lg" hide-footer static>
    <div class="form-group">
      <label>Destination Branch</label>
      <select class="form-control" v-model="destinationBranch" @change="onDestinationChanged">
        <option value="">Select branch</option>
        <option v-for="b in otherBranches" :key="b.id" :value="b.id">{{ b.name }}</option>
      </select>
    </div>

    <div class="form-group" v-if="destinationBranch && branchEmployees.length">
      <label>Received By (optional)</label>
      <select class="form-control" v-model="receivedBy">
        <option value="">Not specified</option>
        <option v-for="e in branchEmployees" :key="e.id" :value="e.id">{{ e.name || e.full_name }}</option>
      </select>
    </div>

    <hr />

    <div class="form-group">
      <label>Add Item</label>
      <input class="form-control" v-model="searchTerm" placeholder="Search item by name" />
      <ul v-if="searchTerm && filteredCatalog.length" class="list-group mt-1" style="max-height:180px; overflow-y:auto;">
        <li
          v-for="p in filteredCatalog"
          :key="p.id"
          class="list-group-item list-group-item-action"
          style="cursor:pointer;"
          @click="addItem(p)"
        >{{ p.product }}</li>
      </ul>
    </div>

    <table class="table table-sm" v-if="items.length">
      <thead>
        <tr>
          <th>Item</th>
          <th>Qty</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(item, index) in items">
          <tr :key="item.id">
            <td>
              {{ item.product }}
              <select v-if="item.has_variants" class="form-control form-control-sm mt-1" v-model="item.variant_id" @change="onSourceVariantChange(item)" required>
                <option value="" disabled>Select source variant</option>
                <option v-for="v in item.sourceVariants" :key="v.id" :value="v.id">{{ v.name }} — {{ v.remaining_units }} in stock</option>
              </select>
              <select v-if="item.sourceUnits.length > 1" class="form-control form-control-sm mt-1" v-model="item.unit_id">
                <option value="">{{ (item.sourceUnits.find(u => u.is_base_unit) || {}).unit_name || 'Base unit' }} (base)</option>
                <option v-for="u in item.sourceUnits.filter(u => !u.is_base_unit)" :key="u.id" :value="u.id">{{ u.unit_name }} (x{{ u.conversion_factor }})</option>
              </select>

              <div v-if="destinationBranch" class="mt-1">
                <small v-if="item.destLoading" class="text-muted">Checking destination…</small>
                <small v-else-if="item.destFound === false" class="text-danger d-block">Not found in destination branch — it will be created there.</small>
                <select v-else-if="item.destHasVariants" class="form-control form-control-sm" v-model="item.destination_variant_id" required>
                  <option value="" disabled>Select destination variant</option>
                  <option v-for="v in item.destVariants" :key="v.id" :value="v.id">{{ v.name }}</option>
                </select>
              </div>
            </td>
            <td><input type="number" min="0.01" step="any" class="form-control form-control-sm" v-model="item.quantity" required /></td>
            <td><a @click="items.splice(index, 1)" class="text-danger"><i class="bi bi-x-circle"></i></a></td>
          </tr>
        </template>
      </tbody>
    </table>

    <div v-if="error" class="text-danger mb-2">{{ error }}</div>

    <div class="d-flex justify-content-end">
      <button type="button" class="btn btn-light mr-2" @click="$refs.modal.hide()">Cancel</button>
      <button type="button" class="btn btn-primary" :disabled="!canSubmit || submitting" @click="submit">
        {{ submitting ? 'Creating…' : 'Create Transfer' }}
      </button>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';

/**
 * Creates a transfer (POST /transfer-stocks) with full variant/unit support
 * — the source variant/unit (how much is leaving, in what unit) and, for a
 * variant-enabled product, the explicit destination variant (Option B — no
 * automatic cross-branch variant matching). See
 * docs/product-variations-units-implementation-plan.md Part 16d.
 */
export default {
  name: 'CreateTransferModal',
  data() {
    return {
      otherBranches: [],
      branchEmployees: [],
      destinationBranch: '',
      receivedBy: '',
      searchTerm: '',
      catalog: [],
      items: [],
      error: '',
      submitting: false,
    };
  },
  computed: {
    filteredCatalog() {
      const term = this.searchTerm.toLowerCase();
      const addedIds = new Set(this.items.map(i => i.id));
      return this.catalog
        .filter(p => !p.is_service && !addedIds.has(p.id) && p.product.toLowerCase().includes(term))
        .slice(0, 8);
    },
    canSubmit() {
      if (!this.destinationBranch || !this.items.length) return false;
      return this.items.every(i => {
        if (!i.quantity || Number(i.quantity) <= 0) return false;
        if (i.has_variants && !i.variant_id) return false;
        if (i.destHasVariants && !i.destination_variant_id) return false;
        return true;
      });
    },
  },
  methods: {
    async open() {
      this.destinationBranch = '';
      this.receivedBy = '';
      this.branchEmployees = [];
      this.searchTerm = '';
      this.items = [];
      this.error = '';
      await this.fetchBranches();
      await this.fetchCatalog();
      this.$refs.modal.show();
    },
    async fetchBranches() {
      try {
        const { data } = await axios.get('/api/other-branches');
        this.otherBranches = data || [];
      } catch (e) {
        this.otherBranches = [];
      }
    },
    async fetchCatalog() {
      try {
        const { data } = await axios.get('/api/only-products');
        this.catalog = data.items || data || [];
      } catch (e) {
        this.catalog = [];
      }
    },
    async onDestinationChanged() {
      this.branchEmployees = [];
      if (this.destinationBranch) {
        try {
          const { data } = await axios.get(`/api/branch-employees/${this.destinationBranch}`);
          this.branchEmployees = data || [];
        } catch (e) {
          this.branchEmployees = [];
        }
      }
      this.items.forEach(item => this.checkDestination(item));
    },
    async addItem(product) {
      const item = {
        id: product.id,
        product: product.product,
        has_variants: !!product.has_variants,
        quantity: 1,
        variant_id: '',
        unit_id: '',
        sourceVariants: [],
        sourceUnits: [],
        destLoading: false,
        destFound: null,
        destHasVariants: false,
        destVariants: [],
        destination_variant_id: '',
      };
      this.items.push(item);
      this.searchTerm = '';

      if (item.has_variants) {
        try {
          const { data } = await axios.get(`/api/products/${item.id}/variants`);
          item.sourceVariants = data.variants || [];
        } catch (e) { /* ignore */ }
      } else {
        try {
          const { data } = await axios.get(`/api/products/${item.id}/units`);
          item.sourceUnits = data || [];
        } catch (e) { /* ignore */ }
      }

      if (this.destinationBranch) this.checkDestination(item);
    },
    async onSourceVariantChange(item) {
      item.unit_id = '';
      item.sourceUnits = [];
      if (!item.variant_id) return;
      try {
        const { data } = await axios.get(`/api/product-variants/${item.variant_id}/units`);
        item.sourceUnits = data || [];
      } catch (e) { /* ignore */ }
    },
    async checkDestination(item) {
      item.destLoading = true;
      item.destFound = null;
      item.destHasVariants = false;
      item.destVariants = [];
      item.destination_variant_id = '';
      try {
        const { data } = await axios.get('/api/transfer-stocks/destination-lookup', {
          params: { destination_branch: this.destinationBranch, product_id: item.id },
        });
        item.destFound = data.found;
        if (data.found) {
          item.destHasVariants = !!data.product.has_variants;
          item.destVariants = data.variants || [];
        }
      } catch (e) {
        item.destFound = null;
      } finally {
        item.destLoading = false;
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.error = '';
      this.submitting = true;
      try {
        await axios.post('/api/transfer-stocks', {
          destination_branch: this.destinationBranch,
          received_by: this.receivedBy || null,
          items: this.items.map(i => ({
            id: i.id,
            quantity: i.quantity,
            variant_id: i.variant_id || null,
            destination_variant_id: i.destination_variant_id || null,
            unit_id: i.unit_id || null,
          })),
        });
        this.$refs.modal.hide();
        this.$emit('created');
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || 'Could not create the transfer.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
